/**
 * ServiceQuoteForm.jsx - the Service Quote's sections, as the workbook laid
 * them out: A project info, the calendar, B team (one row per engineer, days
 * and hours from the calendar), C other lines (work with no dates), D
 * expenses (quantities from the calendar; an Override changes a line, 0
 * removes it; mileage typed), the FCO switch (a field change order: previous
 * overages, in the total budget), E additional option, F scope of work. With
 * Quotes & Rates Edit, the Manager sheet's overrides too (bill rate, expense
 * unit costs). Every number shown comes from the server's calc. Money is in
 * the rate card's currency, the calendar in its work week (GLOBAL KSA: SAR,
 * Saturday to Friday, Friday double time).
 */
import ScheduleEditor from './ScheduleEditor';
import { blankLine, setTeamTask, syncTeam, weekOfSettings } from './quoteDraft';
import {
  Btn,
  Field,
  Grid,
  MUTED,
  NumInput,
  Section,
  SelectInput,
  TextInput,
  currencyOf,
  fmtMoney,
  fmtNum,
  inputStyle,
  td,
  th,
} from './quoteUi';

const MAX_LINES = 10;
const names = (list) => (list || []).map((x) => x.name);

function LineTable({ lines, onChange, calcLines, settings, label, money }) {
  const billTypes = (settings.lists && settings.lists.billTypes) || ['T&L', 'Fixed Fee'];
  const setLine = (i, k, v) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <>
      <datalist id="quote-task-list">
        {names(settings.tasks).map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      {lines.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Task', 'Description', 'Bill type', 'Rate', 'Hours', 'Price', ''].map((h) => (
                <th key={h} style={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const c = calcLines && calcLines[i];
              return (
                <tr key={i}>
                  <td style={{ ...td, width: '20%' }}>
                    <TextInput
                      aria-label={`${label} ${i + 1} task`}
                      list="quote-task-list"
                      value={l.task}
                      onChange={(v) => setLine(i, 'task', v)}
                    />
                  </td>
                  <td style={td}>
                    <TextInput aria-label={`${label} ${i + 1} description`} value={l.desc} onChange={(v) => setLine(i, 'desc', v)} />
                  </td>
                  <td style={{ ...td, width: 110 }}>
                    <SelectInput value={l.billType || 'T&L'} options={billTypes} onChange={(v) => setLine(i, 'billType', v)} />
                  </td>
                  <td style={{ ...td, width: 70 }}>
                    <SelectInput value={l.rate || 'NT'} options={['NT', 'OT', 'DT']} onChange={(v) => setLine(i, 'rate', v)} />
                  </td>
                  <td style={{ ...td, width: 80 }}>
                    <NumInput aria-label={`${label} ${i + 1} hours`} value={l.hours} onChange={(v) => setLine(i, 'hours', v)} />
                  </td>
                  <td style={{ ...td, width: 100, textAlign: 'right', fontSize: 11.5, fontWeight: 600 }}>
                    {c && c.task ? money(c.usd) : ''}
                  </td>
                  <td style={{ ...td, width: 30 }}>
                    <Btn
                      small
                      danger
                      aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
                      onClick={() => onChange(lines.filter((_, j) => j !== i))}
                    >
                      x
                    </Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 6 }}>
        <Btn small disabled={lines.length >= MAX_LINES} onClick={() => onChange([...lines, blankLine()])}>
          + Add line
        </Btn>
      </div>
    </>
  );
}

export default function ServiceQuoteForm({ draft, setDraft, settings, calc, canOverride }) {
  const h = draft.header || {};
  const fco = h.fco === true;
  const cur = currencyOf(settings);
  const money = (v) => fmtMoney(v, cur);
  const week = weekOfSettings(settings);
  const L = week.labels;
  const scheduleHint = week.isDefault
    ? 'The whole assignment, travel days included. Overtime is weekly: the first 40 hours Monday to Saturday are normal time; Sunday and a worked holiday are double time.'
    : `The whole assignment, travel days included. Overtime is weekly: the week starts on ${L.weekStart}, and its first hours up to the threshold are normal time; ${L.sun || 'a worked holiday'}${L.sun ? ' and a worked holiday are' : ' is'} double time.`;
  const lists = settings.lists || {};
  const setH = (k) => (v) => setDraft((d) => ({ ...d, header: { ...d.header, [k]: v } }));
  const setSchedule = (v) =>
    setDraft((d) => {
      const schedule = typeof v === 'function' ? v(d.schedule) : v;
      return { ...d, schedule, team: syncTeam(d.team, schedule.engineers, settings) };
    });
  const setTeam = (i, k, v) =>
    setDraft((d) => ({
      ...d,
      team: d.team.map((r, j) => (j !== i ? r : k === 'task' ? setTeamTask(r, v, settings) : { ...r, [k]: v })),
    }));
  const setExpOverride = (k, v) =>
    setDraft((d) => {
      const overrides = { ...((d.expenses && d.expenses.overrides) || {}) };
      if (v === '') delete overrides[k];
      else overrides[k] = v;
      return { ...d, expenses: { ...d.expenses, overrides } };
    });
  const setUnitOverride = (k, v) =>
    setDraft((d) => {
      const u = { ...((d.header && d.header.unitOverrides) || {}) };
      if (v === '') delete u[k];
      else u[k] = v;
      return { ...d, header: { ...d.header, unitOverrides: u } };
    });
  const team = draft.team || [];
  const ct = (calc && calc.team) || [];
  const expenses = (calc && calc.expenses) || [];
  const overrides = (draft.expenses && draft.expenses.overrides) || {};

  return (
    <div>
      <Section title="A  Project info" hint="Prefilled from the opportunity and its account. Anything can be overtyped.">
        <Grid cols={4}>
          <Field label="Offer No.">
            <TextInput value={h.offerNo} onChange={setH('offerNo')} />
          </Field>
          <Field label="Offer date">
            <TextInput type="date" value={h.offerDate} onChange={setH('offerDate')} />
          </Field>
          <Field label="RFQ / project #">
            <TextInput value={h.rfqNo} onChange={setH('rfqNo')} />
          </Field>
          <Field label="Revision">
            <TextInput value={`R${h.rev || 0}`} disabled onChange={() => {}} />
          </Field>
          <Field label="Project title (subject)" span={2}>
            <TextInput value={h.subject} onChange={setH('subject')} />
          </Field>
          <Field label="Document type" title={fco ? 'An FCO prints FIELD CHANGE ORDER' : undefined}>
            {fco ? (
              <TextInput value="FIELD CHANGE ORDER" disabled onChange={() => {}} />
            ) : (
              <SelectInput value={h.kicker} options={lists.kickers || []} onChange={setH('kicker')} />
            )}
          </Field>
          <Field label="PO type">
            <SelectInput value={h.poType} options={lists.poTypes || ['T&L', 'Fixed Fee', 'Mixed']} onChange={setH('poType')} />
          </Field>
          <Field label="Customer" span={2}>
            <TextInput value={h.customer} onChange={setH('customer')} />
          </Field>
          <Field label="Address line 1">
            <TextInput value={h.addr1} onChange={setH('addr1')} />
          </Field>
          <Field label="City, ST ZIP">
            <TextInput value={h.addr2} onChange={setH('addr2')} />
          </Field>
          <Field label="Salutation (Dear ...)">
            <TextInput value={h.salutation} onChange={setH('salutation')} placeholder="Sir or Madam" />
          </Field>
          <Field label="End user / site">
            <TextInput value={h.endUser} onChange={setH('endUser')} />
          </Field>
          <Field label="PO number (if issued)">
            <TextInput value={h.poNumber} onChange={setH('poNumber')} />
          </Field>
          <Field label="Valid (days)">
            <NumInput value={h.validity} onChange={setH('validity')} />
          </Field>
          <Field label="Payment terms" span={2}>
            <TextInput value={h.payTerms} onChange={setH('payTerms')} />
          </Field>
          <Field label="Invoicing" span={2}>
            <TextInput value={h.invoicing} onChange={setH('invoicing')} placeholder="monthly" />
          </Field>
          <Field label="Rate group" title="The customer group's bill rate; blank = the default rate">
            <SelectInput value={h.rateGroup} blank="Default rate" options={names(settings.rateGroups)} onChange={setH('rateGroup')} />
          </Field>
          <Field label="Site location" title="Expenses are estimated for this location">
            <SelectInput value={h.siteLocation} options={names(settings.locations)} onChange={setH('siteLocation')} />
          </Field>
          <Field label="Prepared by">
            <TextInput value={h.preparer} onChange={setH('preparer')} />
          </Field>
          <Field label="Preparer phone">
            <TextInput value={h.prepPhone} onChange={setH('prepPhone')} />
          </Field>
          <Field label="Preparer email" span={2}>
            <TextInput value={h.prepEmail} onChange={setH('prepEmail')} />
          </Field>
        </Grid>
      </Section>

      <Section title="Schedule" hint={scheduleHint}>
        <ScheduleEditor
          schedule={draft.schedule}
          onChange={setSchedule}
          calcSchedule={calc && calc.schedule}
          roles={team.map((r) => r.role)}
          week={week}
        />
      </Section>

      <Section title="B  Team" hint="One row per engineer. Days and hours come from the calendar.">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Engineer / role', 'Task', 'Bill type', 'Expenses', 'Days', 'NT', 'OT', 'DT', 'Travel', 'Hours', 'Price'].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {team.map((r, i) => {
              const c = ct[i];
              const num = (v) => <td style={{ ...td, textAlign: 'right', fontSize: 11.5, width: 44 }}>{c ? fmtNum(v) : ''}</td>;
              return (
                <tr key={i}>
                  <td style={td}>
                    <TextInput aria-label={`Engineer ${i + 1} role`} value={r.role} onChange={(v) => setTeam(i, 'role', v)} />
                  </td>
                  <td style={td}>
                    <SelectInput
                      aria-label={`Engineer ${i + 1} task`}
                      value={r.task}
                      blank="-"
                      options={names(settings.tasks)}
                      onChange={(v) => setTeam(i, 'task', v)}
                    />
                  </td>
                  <td style={{ ...td, width: 96 }}>
                    <SelectInput
                      value={r.billType}
                      options={lists.billTypes || ['Fixed Fee', 'T&L']}
                      onChange={(v) => setTeam(i, 'billType', v)}
                    />
                  </td>
                  <td style={td}>
                    <SelectInput
                      aria-label={`Engineer ${i + 1} expenses`}
                      value={r.pkg}
                      options={names(settings.packages)}
                      onChange={(v) => setTeam(i, 'pkg', v)}
                    />
                  </td>
                  {num(c && c.days)}
                  {num(c && c.nt)}
                  {num(c && c.ot)}
                  {num(c && c.dt)}
                  {num(c && c.travel)}
                  {num(c && c.totalHours)}
                  <td style={{ ...td, textAlign: 'right', fontSize: 11.5, fontWeight: 600, width: 96 }}>{c ? money(c.usd) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section
        title="C  Other lines"
        hint="Work with no dates: fixed fee, offline programming, remote support. Fixed Fee and Offline lines print a price only, never hours."
      >
        <LineTable
          label="Other line"
          lines={draft.other || []}
          settings={settings}
          calcLines={calc && calc.other}
          money={money}
          onChange={(other) => setDraft((d) => ({ ...d, other }))}
        />
      </Section>

      <Section
        title="D  Expenses"
        hint="Quantities come from the calendar: airfare is round trips, every other row is days on site (travel days included). Type an Override to change a line, 0 to remove it. The handling fee is folded into each price."
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Expense', 'From calendar', 'Override', 'Unit price', 'Amount'].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.key} style={{ opacity: e.printed ? 1 : 0.6 }}>
                <td style={{ ...td, fontSize: 12, fontWeight: 600 }}>
                  {e.label}
                  {e.key === 'mileage' ? ' (round-trip miles)' : ''}
                </td>
                <td style={{ ...td, textAlign: 'right', fontSize: 11.5, width: 110 }}>{e.key === 'mileage' ? '' : fmtNum(e.auto)}</td>
                <td style={{ ...td, width: 110 }}>
                  {e.key === 'mileage' ? (
                    <NumInput
                      aria-label="Mileage"
                      value={draft.expenses && draft.expenses.mileage}
                      onChange={(v) => setDraft((d) => ({ ...d, expenses: { ...d.expenses, mileage: v } }))}
                    />
                  ) : (
                    <NumInput
                      aria-label={`${e.label} override`}
                      value={overrides[e.key] ?? ''}
                      placeholder="auto"
                      onChange={(v) => setExpOverride(e.key, v)}
                    />
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', fontSize: 11.5, width: 110 }}>{money(e.unitPrice)}</td>
                <td style={{ ...td, textAlign: 'right', fontSize: 11.5, fontWeight: 600, width: 110 }}>
                  {e.printed ? money(e.amount) : <span style={{ color: MUTED }}>left off</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section
        title="Field change order (FCO)"
        hint="For a change to a job already quoted: the document says FCO, and the previous overages print as their own row between the expenses and the TOTAL BUDGET, which includes them."
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: fco ? 10 : 0,
          }}
        >
          <input type="checkbox" checked={fco} onChange={(e) => setH('fco')(e.target.checked)} />
          This is an FCO (field change order)
        </label>
        {fco && (
          <Grid cols={4}>
            <Field label={`Previous overages (${cur.symbol})`}>
              <NumInput aria-label="Previous overages" value={h.prevOverages} placeholder="0.00" onChange={setH('prevOverages')} />
            </Field>
            <Field label="Changes to quote #" title="The original offer this FCO changes (optional)">
              <TextInput aria-label="Changes to quote" value={h.fcoRef} onChange={setH('fcoRef')} placeholder="e.g. GAT-B-7101" />
            </Field>
            <Field label="What the overages cover (optional)" span={2}>
              <TextInput
                aria-label="What the overages cover"
                value={h.overagesNote}
                onChange={setH('overagesNote')}
                placeholder="Printed beside the PREVIOUS OVERAGES row"
              />
            </Field>
          </Grid>
        )}
      </Section>

      <Section title="E  Additional option" hint='Priced separately in the document as "Additional option"; not in the quote total.'>
        <LineTable
          label="Option line"
          lines={draft.option || []}
          settings={settings}
          calcLines={calc && calc.option}
          money={money}
          onChange={(option) => setDraft((d) => ({ ...d, option }))}
        />
      </Section>

      <Section title="F  Scope of work" hint="Printed as section 1.0. Each line is its own paragraph.">
        <textarea
          aria-label="Scope of work"
          value={draft.scope || ''}
          rows={6}
          onChange={(e) => {
            const v = e.target.value;
            setDraft((d) => ({ ...d, scope: v }));
          }}
          placeholder="Scope of work per the customer request."
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
        />
      </Section>

      {canOverride && (
        <Section
          title="Manager overrides"
          hint="Quotes & Rates Edit only (the workbook's Manager sheet). Blank = the rate group / the site location's estimate."
        >
          <Grid cols={5}>
            <Field label={`Bill rate (${cur.symbol}/hr)`}>
              <NumInput
                aria-label="Bill rate override"
                value={h.rateOverride}
                placeholder={calc ? String(calc.rate) : ''}
                onChange={setH('rateOverride')}
              />
            </Field>
            {expenses.map((e) => (
              <Field key={e.key} label={`${e.label} (${cur.symbol})`}>
                <NumInput
                  value={(h.unitOverrides || {})[e.key] ?? ''}
                  placeholder={String(e.unit)}
                  onChange={(v) => setUnitOverride(e.key, v)}
                />
              </Field>
            ))}
          </Grid>
        </Section>
      )}
    </div>
  );
}
